"use client";
import {
  CustomButton,
  FlexBetween,
  FlexBetweenCol,
  FlexCol,
  Logo,
  TextField,
} from "@/components";
import {
  IServiceFormProps,
  IUserRoleBase,
  TBouncedCheckInquiryInfer,
  TRawServiceApiKeys,
} from "@/interfaces";
import { cn } from "@/lib";
import { zodResolver } from "@hookform/resolvers/zod";

import React, { JSX, useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSession } from "next-auth/react";
import { useAppStore, useModalStore, useSecureStore } from "@/store";
import {
  useBouncedCheckDetailsInquiryMutation,
  useBouncedCheckInquiryMutation,
} from "@/hooks/useInquiry";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { bouncedCheckInquirySchema } from "@/validations";
import { useTranslations } from "next-intl";
import { routes } from "@/routes";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import HtmlRenderer from "../../htmlRenderer";
import MainLogo from "@public/svg/logo-2.svg";
import Loader from "@/components/ui/loader";
import PaymentModalContainer from "@/components/Payment/paymentModalContainer";
import { IBouncedCheckInquiryMutationPayload } from "@/interfaces/inquiries/bank";
import SampleInquiryModal from "@/components/inquirySamples/sampleInquiryModal";
import BouncedCheckSample from "@/components/inquirySamples/bouncedCheckSample";
import MilitaryRelatedServicesModal from "@/components/militaryRelatedServicesModal";
import { IMilitaryInquiryItem } from "@/interfaces";
import { BdBillList, BdDocumentText } from "solar-icon-react/bd";

const toggleRoleObj: {
  label: string;
  role: IUserRoleBase;
  isSelected: boolean;
  disable: boolean;
}[] = [
  { label: "حقیقی", role: "1", isSelected: true, disable: false },
  { label: "حقوقی", role: "2", isSelected: false, disable: true },
];

const bouncedCheckIconMap: Record<string, JSX.Element> = {
  returned_check: <BdBillList className="size-full" />,
  returned_check_details: <BdDocumentText className="size-full" />,
  back_cheques: <BdDocumentText className="size-full" />,
};

const bouncedCheckTitleMap: Record<string, string> = {
  returned_check: "استعلام تعداد چک برگشتی",
  returned_check_details: "استعلام چک برگشتی",
  back_cheques: "استعلام چک برگشتی با جزئیات کامل",
};

const bouncedCheckDescriptionMap: Record<string, string> = {
  returned_check:
    "فقط تعداد چک‌های برگشتی ثبت‌شده برای کد ملی واردشده را نمایش می‌دهد.",
  returned_check_details:
    "تعداد و جزئیات مبلغی چک‌های برگشتی ثبت‌شده برای کد ملی واردشده را نمایش می‌دهد.",
  back_cheques: "جزئیات کامل چک‌های برگشتی را پس از تایید پیامکی نمایش می‌دهد.",
};

const BACK_CHEQUES_FULL_PATH = "/services/استعلام-چک-برگشتی-با-جزئیات-کامل";

const BouncedCheckInquiryForm = ({
  cost,
  isLoggedIn,
  navigateToLogin,
  announcement,
  disableService,
  serviceId,
  relatedServices = [],
}: IServiceFormProps) => {
  const t = useTranslations("general");
  const { data: session } = useSession();
  const { persistedFields, setPersistedFields } = useSecureStore();
  const { setPostLoginAction } = useAppStore();
  const [toggleUserRole, setToggleUserRole] = useState(toggleRoleObj);

  const {
    isPending: isSubmitting,
    mutateAsync: bouncedCheckInquiryFn,
    error: bouncedCheckInquiryError,
    reset: resetBouncedCheckInquiry,
  } = useBouncedCheckInquiryMutation(session?.user.token);
  const {
    isPending: isSubmittingDetails,
    mutateAsync: bouncedCheckDetailsInquiryFn,
    error: bouncedCheckDetailsInquiryError,
    reset: resetBouncedCheckDetailsInquiry,
  } = useBouncedCheckDetailsInquiryMutation(session?.user.token);
  const router = useRouter();
  const { openModal, closeModal, setOpenModal } = useModalStore();
  const isPaymentMethodModalOpen = openModal === "payment-method";
  const isServiceSelectModalOpen =
    openModal === "military-related-services-modal";
  const openPaymentModal = () => {
    setOpenModal("military-related-services-modal");
  };

  const serviceKey = TRawServiceApiKeys.Bounced_Check;
  const SELECTED_INQUIRY_KEY = `${serviceKey}_selectedInquiry`;
  const mappedRelatedServices: IMilitaryInquiryItem[] = useMemo(() => {
    const services = relatedServices.length
      ? relatedServices
      : [
          {
            id: serviceId || 0,
            name: bouncedCheckTitleMap.returned_check,
            price_user: cost,
            flag: "returned_check",
            status: disableService ? "inactive" : "active",
          } as any,
        ];

    const mappedServices = services.map((service) => ({
      id: service.id,
      name: bouncedCheckTitleMap[service.flag] || service.name,
      price: service.price_user,
      description:
        bouncedCheckDescriptionMap[service.flag] || service.description,
      flag: service.flag,
      status: service.status,
      icon: bouncedCheckIconMap[service.flag] ?? null,
      key: service.flag,
    }));

    return mappedServices;
  }, [cost, disableService, relatedServices, serviceId]);

  const [selectedInquiry, setSelectedInquiry] = useState<IMilitaryInquiryItem>(
    () => {
      const persisted = persistedFields?.[SELECTED_INQUIRY_KEY];
      if (persisted) {
        const found = mappedRelatedServices.find(
          (service) => service.flag === persisted.flag,
        );
        return found || mappedRelatedServices[0];
      }
      return mappedRelatedServices[0];
    },
  );

  const activeKey = useMemo(
    () => ({
      id: String(selectedInquiry?.id || ""),
      label:
        selectedInquiry?.name ||
        globalRawServicesData.Bounced_Check.label.adapted,
      key: selectedInquiry?.key || globalRawServicesData.Bounced_Check.key,
      apiKey: (selectedInquiry?.flag ||
        globalRawServicesData.Bounced_Check.api.apiKey) as TRawServiceApiKeys,
      href: routes.services.Bounced_Check.rewritePath,
      disable: selectedInquiry?.status !== "active",
    }),
    [selectedInquiry],
  );

  const {
    control,
    handleSubmit,
    formState: { errors: formError },
    getValues,
    setValue,
    trigger,
  } = useForm<TBouncedCheckInquiryInfer>({
    resolver: zodResolver(bouncedCheckInquirySchema),
    mode: "onBlur", // Only validate when user leaves the field
    defaultValues: {
      nationalCode: persistedFields?.[serviceKey]?.nationalCode || "",
      nationalId: persistedFields?.[serviceKey]?.nationalId || "",
      role: "1",
    },
  });

  const pathname = usePathname();
  const searchParams = useSearchParams();
  useEffect(() => {
    if (searchParams?.get("payment") === "1") {
      setOpenModal("payment-method");
    }
  }, [searchParams, setOpenModal]);

  const onSubmit = (data: any) => {
    setError(null);
    if (!isLoggedIn) {
      setPostLoginAction("open-payment-modal");
      navigateToLogin();
      return;
    }
    openPaymentModal();
  };

  const selectedRole: IUserRoleBase =
    (toggleUserRole.find((role) => role.isSelected)?.role as IUserRoleBase) ||
    "1";

  const [packageId, setPackageId] = useState<string | null>(null);
  // Handle package selection
  const handlePackageSelect = useCallback((packageId: string) => {
    setPackageId(packageId);
  }, []);

  useEffect(() => {
    setValue("role", selectedRole);
  }, [selectedRole, setValue]);

  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timerEndTime, setTimerEndTime] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSampleOpen, setIsSampleOpen] = useState(false);

  const paymentSubmission = useCallback(async () => {
    const formData = getValues();
    const nationalCode =
      selectedRole === "1" ? formData.nationalCode : formData.nationalId;

    if (!nationalCode) {
      console.error("National code is required");
      return;
    }

    const payload: Readonly<IBouncedCheckInquiryMutationPayload> = {
      national_code: nationalCode,
      customer_type: selectedRole,
      campaign: packageId || undefined,
    };
    try {
      const data =
        String(selectedInquiry?.flag) === "returned_check_details"
          ? await bouncedCheckDetailsInquiryFn({ payload })
          : await bouncedCheckInquiryFn({ payload });

      const purchase_id = data.data?.purchase_id;
      if (purchase_id) {
        router.push(
          `${routes.main.INQUIRY_DETAILS.primaryPath}/${data.data?.purchase_id}`,
        );
        closeModal();
      }
      setError(null);
      setIsTimerActive(false);
      setTimerEndTime(null);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      }
      setIsTimerActive(true);
      setTimerEndTime(Date.now() + 60000);
    }
  }, [
    bouncedCheckDetailsInquiryFn,
    bouncedCheckInquiryFn,
    closeModal,
    getValues,
    packageId,
    router,
    selectedInquiry?.flag,
    selectedRole,
  ]);

  useEffect(() => {
    if (isPaymentMethodModalOpen) {
      resetBouncedCheckInquiry();
      resetBouncedCheckDetailsInquiry();
    }
  }, [
    isPaymentMethodModalOpen,
    resetBouncedCheckDetailsInquiry,
    resetBouncedCheckInquiry,
  ]);

  const handleTimerComplete = () => {
    setIsTimerActive(false);
    setTimerEndTime(null);
  };

  const resetBouncedCheckErrors = useCallback(() => {
    resetBouncedCheckInquiry();
    resetBouncedCheckDetailsInquiry();
  }, [resetBouncedCheckDetailsInquiry, resetBouncedCheckInquiry]);

  const handleInquirySelect = (inquiry: IMilitaryInquiryItem) => {
    if (String(inquiry.flag) === "back_cheques") {
      const nationalCode = getValues("nationalCode");
      const query = nationalCode
        ? `?national_code=${encodeURIComponent(nationalCode)}`
        : "";

      closeModal();
      router.push(`${BACK_CHEQUES_FULL_PATH}${query}`);
      return;
    }

    setSelectedInquiry(inquiry);
    setPersistedFields({
      ...persistedFields,
      [SELECTED_INQUIRY_KEY]: inquiry,
    });
  };

  const handleInquirySubmit = () => {
    if (String(selectedInquiry?.flag) === "back_cheques") {
      const nationalCode = getValues("nationalCode");
      const query = nationalCode
        ? `?national_code=${encodeURIComponent(nationalCode)}`
        : "";

      closeModal();
      router.push(`${BACK_CHEQUES_FULL_PATH}${query}`);
      return;
    }

    closeModal();
    setOpenModal("payment-method");
  };

  const handleBackToServiceSelect = () => {
    closeModal();
    setOpenModal("military-related-services-modal");
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FlexBetweenCol gap={8} className={cn("w-full")}>
          <FlexCol gap={6} className="mb-8 w-full lg:max-w-[80%]">
            {/*<FlexBetween gap={3} className="xl:gap-6">*/}
            {/*  {toggleUserRole.map(*/}
            {/*    ({ label, isSelected, role, disable }, idx) => {*/}
            {/*      return (*/}
            {/*        <ToggleCard*/}
            {/*          key={idx}*/}
            {/*          className={cn(*/}
            {/*            "hover:text-secondary-main shadow-main! h-10 grow border-gray-300 bg-white py-0 transition-colors xl:h-12 xl:px-4",*/}
            {/*            isSelected &&*/}
            {/*              "border-primary-main shadow-primary-main!",*/}
            {/*          )}*/}
            {/*          isSelected={isSelected}*/}
            {/*          disable={disable}*/}
            {/*          nodeEnd={<CheckBox isChecked={isSelected} />}*/}
            {/*          setSelected={() => {*/}
            {/*            setToggleUserRole((prev) =>*/}
            {/*              prev.map((item) =>*/}
            {/*                item.role === role*/}
            {/*                  ? { ...item, isSelected: true }*/}
            {/*                  : { ...item, isSelected: false },*/}
            {/*              ),*/}
            {/*            );*/}

            {/*            // Clear the appropriate field when role changes*/}
            {/*            if (role === "1") {*/}
            {/*              // Switching to individual - clear nationalId field*/}
            {/*              setValue("nationalId", "");*/}
            {/*            } else {*/}
            {/*              // Switching to legal entity - clear nationalCode field*/}
            {/*              setValue("nationalCode", "");*/}
            {/*            }*/}
            {/*          }}*/}
            {/*        >*/}
            {/*          <p>{label}</p>*/}
            {/*        </ToggleCard>*/}
            {/*      );*/}
            {/*    },*/}
            {/*  )}*/}
            {/*</FlexBetween>*/}
            {selectedRole === "1" ? (
              <Controller
                name={"nationalCode"}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    hasShadow
                    aria-label="nationalCode"
                    maxLength={10}
                    numeric
                    inputMode="numeric"
                    placeholder={t("nationalCode")}
                    inputClassName={cn("tracking-widest h-10")}
                    error={formError.nationalCode?.message}
                  />
                )}
              />
            ) : (
              <Controller
                name={"nationalId"}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    hasShadow
                    aria-label="nationalId"
                    maxLength={10}
                    numeric
                    inputMode="numeric"
                    placeholder="کدملی یکی از صاحبین امضاء"
                    inputClassName={cn("tracking-widest h-10")}
                    error={formError.nationalId?.message}
                  />
                )}
              />
            )}
          </FlexCol>
          {announcement && <HtmlRenderer htmlString={announcement} />}
          <FlexBetween gap={2} className="lg:gap-4">
            {/*<CustomButton*/}
            {/*  id="bounced-check-inquiry-sample"*/}
            {/*  type="button"*/}
            {/*  className="border-primary-main text-primary-main hover:bg-primary-main/5 border bg-transparent !px-3 !text-sm"*/}
            {/*  onClick={() => setIsSampleOpen(true)}*/}
            {/*>*/}
            {/*  نمونه نتیجه*/}
            {/*</CustomButton>*/}
            <CustomButton
              id="bounced-check-inquiry-submit"
              disabled={disableService}
              className="flex-1"
              variant="primary"
              type="submit"
            >
              استعلام
            </CustomButton>
          </FlexBetween>
        </FlexBetweenCol>
      </form>
      {isPaymentMethodModalOpen && (
        <PaymentModalContainer
          payBtnId="payment-bounced-check-inquiry"
          submissionError={
            String(selectedInquiry?.flag) === "returned_check_details"
              ? bouncedCheckDetailsInquiryError
              : bouncedCheckInquiryError
          }
          paymentSubmission={paymentSubmission}
          isOpen={isPaymentMethodModalOpen}
          onClose={closeModal}
          selectedService={activeKey}
          submissionLoading={isSubmitting || isSubmittingDetails}
          onErrorReset={resetBouncedCheckErrors}
          onPackageSelect={handlePackageSelect}
          serviceId={selectedInquiry?.id || serviceId}
          showData="service"
          handleBackBtn={handleBackToServiceSelect}
          persistedData={{
            [serviceKey]: {
              ...getValues(),
            },
            [SELECTED_INQUIRY_KEY]: selectedInquiry,
          }}
          showManualError={error}
          isTimerActive={isTimerActive}
          timerEndTime={timerEndTime}
          onTimerComplete={handleTimerComplete}
        />
      )}
      {isServiceSelectModalOpen && (
        <MilitaryRelatedServicesModal
          onSubmit={handleInquirySubmit}
          services={mappedRelatedServices}
          isOpen={isServiceSelectModalOpen}
          onClose={closeModal}
          onInquirySelect={handleInquirySelect}
          selectedInquiry={selectedInquiry}
          setSampleService={() => setIsSampleOpen(true)}
        />
      )}
      {isSampleOpen && (
        <SampleInquiryModal
          onClose={() => setIsSampleOpen(false)}
          title="نمونه نتیجه استعلام چک برگشتی"
        >
          <BouncedCheckSample />
        </SampleInquiryModal>
      )}
      {(isSubmitting || isSubmittingDetails) && (
        <div className="flex-center fixed inset-0 z-[1000] flex bg-gray-900/60">
          <div className="flex-column flex-center flex w-60 rounded-xl bg-white p-3 sm:w-80 sm:p-4">
            <Logo className="mb-3" src={MainLogo} width={60} height={60} />
            <h2 className="text-center text-sm font-semibold sm:text-base">
              درحال استعلام <br /> لطفا شکیبا باشید
            </h2>
            <div className="mt-4 size-4 sm:size-5">
              <Loader color="bg-primary-main" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BouncedCheckInquiryForm;
